import { IconTags, IconSettings, IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import Parser from "@helpers/parser";
import { Card, Text, Button } from "@fragments";

import "./styles.scss";

const OWNER_ROLES = ["owner", "creator"];

export default function PlansSidebar({ community, className = "" }) {
  const { t } = useTranslation("community");

  const isOwner = OWNER_ROLES.includes(community?.role);
  const hasPlans = Boolean(community?.plans?.length);

  if (!hasPlans && !isOwner) return null;

  function handleOpenPlansManagement() {
    window.navigate(`${community.slug}/settings/pricing`);
  }

  function handleCreatePlan() {
    window.navigate(`${community.slug}/settings/pricing?creatingPlan=true`);
  }

  return (
    <Card className={`plans-sidebar ${className}`.trim()} flat noBodyPadding>
      {community?.banner && (
        <div className="plans-sidebar-backdrop" aria-hidden="true">
          <img src={community.banner} alt="" />
        </div>
      )}

      <Text.h4
        className="plans-sidebar-header px-3 pt-3"
        left={<IconTags aria-hidden="true" />}
        weight={400}
        contrast={8}
        grow
        right={
          hasPlans && isOwner ? (
            <Button
              variant="rounded"
              color="overlay"
              size="small"
              icon={<IconSettings />}
              tooltip={t("plansSidebar.managePlans")}
              aria-label={t("plansSidebar.managePlans")}
              onClick={handleOpenPlansManagement}
            />
          ) : null
        }
      >
        {t("plansSidebar.title")}
      </Text.h4>
      <Text.span
        className="px-3 pt-1"
        weight={200}
        contrast={6}
      >
        {hasPlans
          ? t("plansSidebar.subtitleWithPlans", { name: community.name })
          : t("plansSidebar.subtitleEmpty")}
      </Text.span>

      <div className="plans-sidebar-scroller">
        {hasPlans ? (
          community.plans.map((plan) => (
            <PlanBox key={plan.id} plan={plan} community={community} />
          ))
        ) : (
          <EmptyPlanBox community={community} onCreate={handleCreatePlan} />
        )}
      </div>
    </Card>
  );
}

function PlanBox({ plan, community }) {
  const { t } = useTranslation("community");

  const priceValue = Parser.price.brl(
    plan.installments_list?.[plan.installments - 1] / plan.installments ||
      plan.price,
  );
  const interval = Parser.interval.noun(plan.interval).toLowerCase();

  function handleOpenCheckout() {
    window.open(
      `https://pay.ginastee.com/${community.checkout}?plan=${plan.id}`,
      "_blank",
    );
  }

  return (
    <article className="ticket-wrap">
      <div className="ticket ticket-top">
        <div className="ticket-header">
          {community?.banner ? (
            <img src={community.banner} alt="" className="banner" />
          ) : (
            <div className="banner banner--fallback" aria-hidden="true" />
          )}

          <Text.h5 weight={500} contrast={9} maxLines={1} grow>
            {plan.label}
          </Text.h5>
        </div>

        <div className="ticket-price">
          {plan.installments > 1 && (
            <Text.span contrast={5}>{t("plansSidebar.installments", { value: plan.installments })}</Text.span>
          )}

          <div className="price-value">
            <Text.h1 weight={500} contrast={9}>
              R${priceValue}
            </Text.h1>
            {plan.interval !== "lifetime" && (
              <Text.span contrast={6}>{t("plansSidebar.perInterval", { interval })}</Text.span>
            )}
          </div>

          {plan.interval === "lifetime" && (
            <Text.span contrast={6}>{t("plansSidebar.lifetimeAccess")}</Text.span>
          )}
        </div>
      </div>

      <Button
        fullWidth
        color="inverse"
        className="ticket-btn"
        onClick={handleOpenCheckout}
      >
        {t("plansSidebar.buyPlan", { label: plan.label })}
      </Button>
    </article>
  );
}

function EmptyPlanBox({ community, onCreate }) {
  const { t } = useTranslation("community");

  return (
    <article className="ticket-wrap ticket-wrap--empty">
      <div className="ticket ticket-top">
        <div className="ticket-header">
          {community?.banner ? (
            <img src={community.banner} alt="" className="banner" />
          ) : (
            <div className="banner banner--fallback" aria-hidden="true" />
          )}

          <Text.h5 weight={500} contrast={9} maxLines={1} grow>
            Premium
          </Text.h5>
        </div>

        <div className="ticket-price">
          <div className="price-value">
            <Text.h1 weight={500} contrast={6}>
              R$ --
            </Text.h1>
          </div>
          <Text.span contrast={5}>
            {t("plansSidebar.emptyDescription")}
          </Text.span>
        </div>
      </div>

      <Button
        fullWidth
        color="inverse"
        className="ticket-btn"
        startIcon={<IconPlus />}
        onClick={onCreate}
      >
        {t("plansSidebar.createFirstPlan")}
      </Button>
    </article>
  );
}
